/**
 * Locale-aware number engine for the financial NumberInput. The canonical value
 * is always a plain decimal string with "." as the decimal separator and no
 * grouping (e.g. "-1234.5") — safe to send to an API and free of float error.
 * Display strings are derived per-locale (grouping + decimal separator + optional
 * currency). No third-party dependency: built on Intl.NumberFormat.
 */

export interface Separators {
  group: string;
  decimal: string;
}

const sepCache = new Map<string, Separators>();

/** Discover a locale's grouping + decimal separators via Intl. */
export function localeSeparators(locale: string): Separators {
  const hit = sepCache.get(locale);
  if (hit) return hit;
  let group = ",";
  let decimal = ".";
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    for (const p of parts) {
      if (p.type === "group") group = p.value;
      else if (p.type === "decimal") decimal = p.value;
    }
  } catch {
    /* fall back to en-US */
  }
  const sep = { group, decimal };
  sepCache.set(locale, sep);
  return sep;
}

/** Currency symbol + whether it precedes (prefix) or follows (suffix) the amount. */
export function currencyAffix(locale: string, currency: string): { symbol: string; prefix: boolean } {
  try {
    const parts = new Intl.NumberFormat(locale, { style: "currency", currency }).formatToParts(1);
    const i = parts.findIndex((p) => p.type === "currency");
    const numIndex = parts.findIndex((p) => p.type === "integer");
    const symbol = i >= 0 ? parts[i]!.value : currency;
    return { symbol, prefix: i >= 0 && numIndex >= 0 ? i < numIndex : true };
  } catch {
    return { symbol: currency, prefix: true };
  }
}

function groupInteger(intDigits: string, group: string): string {
  // intDigits: only digits, no sign. Group from the right in threes.
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, group);
}

export interface FormatOptions {
  locale: string;
  decimals?: number | undefined; // fixed max fraction digits (pads on blur)
  grouping?: boolean;
  padFraction?: boolean; // pad fraction to `decimals` (used on blur)
}

/** Canonical decimal string → locale display string (no currency/affix here). */
export function formatCanonical(canonical: string, opts: FormatOptions): string {
  if (canonical === "" || canonical === "-") return "";
  const { group, decimal } = localeSeparators(opts.locale);
  const neg = canonical.startsWith("-");
  const unsigned = neg ? canonical.slice(1) : canonical;
  let [intPart = "", fracPart = ""] = unsigned.split(".");
  intPart = intPart.replace(/^0+(?=\d)/, "") || "0";

  if (opts.decimals != null) {
    fracPart = fracPart.slice(0, opts.decimals);
    if (opts.padFraction) fracPart = fracPart.padEnd(opts.decimals, "0");
  }

  const intOut = opts.grouping === false ? intPart : groupInteger(intPart, group);
  const out = fracPart ? `${intOut}${decimal}${fracPart}` : intOut;
  return neg ? `-${out}` : out;
}

/**
 * A user-typed display string → canonical decimal string. Strips grouping, maps
 * the locale decimal separator to ".", and drops anything else.
 */
export function toCanonical(display: string, locale: string, allowNegative = true): string {
  const { group, decimal } = localeSeparators(locale);
  const neg = allowNegative && display.includes("-");
  let s = display;
  if (group) s = s.split(group).join(""); // strip grouping first (may equal "." in de/tr)
  if (decimal && decimal !== ".") s = s.split(decimal).join("."); // locale decimal → "."
  s = s.replace(/[^\d.]/g, ""); // keep digits + dot only
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, ""); // only the first dot
  }
  if (s === "" || s === ".") return neg ? "-" : "";
  return neg ? `-${s}` : s;
}

/** Constrain a canonical value to [min, max]. Empty/partial passes through. */
export function clampCanonical(canonical: string, min?: number, max?: number): string {
  if (canonical === "" || canonical === "-" || canonical.endsWith(".")) return canonical;
  const n = Number(canonical);
  if (Number.isNaN(n)) return canonical;
  if (min != null && n < min) return String(min);
  if (max != null && n > max) return String(max);
  return canonical;
}

/** Add `step` to a canonical value, respecting decimals + bounds. */
export function stepCanonical(
  canonical: string,
  step: number,
  decimals: number | undefined,
  min?: number,
  max?: number,
): string {
  const base = canonical === "" || canonical === "-" ? 0 : Number(canonical);
  const n = (Number.isNaN(base) ? 0 : base) + step;
  const fixed = decimals != null ? n.toFixed(decimals) : String(n);
  return clampCanonical(fixed, min, max);
}
